import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ListChecks, Check, Trash, Plus, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const TABS = ["My Tasks", "Assigned", "Assign a task"];
const today = () => format(new Date(), "yyyy-MM-dd");
const blank = () => ({
  title: "",
  notes: "",
  job_id: "none",
  date: today(),
  assignee: "none",
});

export const TasksPopover = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("My Tasks");
  const [tasks, setTasks] = useState([]);
  const [crew, setCrew] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [draft, setDraft] = useState(blank());
  const [busy, setBusy] = useState(false);

  const me = user?.name || user?.email || "";

  const load = useCallback(async () => {
    const [t, c, j] = await Promise.all([
      api.get("/tasks", { params: { date: today(), carry: true } }),
      api.get("/crew-list"),
      api.get("/jobs"),
    ]);
    setTasks(t.data);
    setCrew(c.data);
    setJobs(j.data);
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const mine = useMemo(
    () => tasks.filter((t) => !t.assignee || t.assignee === me),
    [tasks, me],
  );
  const assigned = useMemo(
    () => tasks.filter((t) => t.assignee && t.assignee !== me),
    [tasks, me],
  );
  const openCount = mine.filter((t) => !t.done).length;
  const seenKey = `bg_tasks_seen_${me}`;
  const [lastSeen, setLastSeen] = useState(
    () => localStorage.getItem(seenKey) || "",
  );
  const newlyAssigned = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.assignee === me &&
          !t.done &&
          t.created_by &&
          t.created_by !== (user?.email || "") &&
          (!lastSeen || (t.created_at || "") > lastSeen),
      ),
    [tasks, me, lastSeen, user],
  );

  const markSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem(seenKey, now);
    setLastSeen(now);
  };

  const add = async (e, assignToMe) => {
    e.preventDefault();
    setBusy(true);
    const job = jobs.find((x) => x.id === draft.job_id);
    try {
      await api.post("/tasks", {
        title: draft.title,
        notes: draft.notes,
        date: draft.date || today(),
        assignee: assignToMe
          ? me
          : draft.assignee === "none"
            ? ""
            : draft.assignee,
        job_id: draft.job_id === "none" ? null : draft.job_id,
        job_code: job?.code || "",
      });
      setDraft(blank());
      toast.success("Task added");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not add task",
      );
    }
    setBusy(false);
  };

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}/done`, { done: !t.done });
    load();
  };

  const remove = async (t) => {
    await api.delete(`/tasks/${t.id}`);
    load();
  };

  const row = (t) => (
    <div
      key={t.id}
      data-testid={`pop-task-${t.id}`}
      className={`flex items-start gap-3 rounded-md border p-3 ${
        t.done
          ? "border-emerald-500/25 bg-zinc-950"
          : "border-zinc-800 bg-zinc-950"
      }`}
    >
      <button
        data-testid={`pop-task-toggle-${t.id}`}
        onClick={() => toggle(t)}
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded border transition-colors duration-150 ${
          t.done
            ? "border-emerald-500 bg-emerald-500 text-black"
            : "border-zinc-600 hover:border-indigo-400"
        }`}
      >
        {t.done && <Check size={13} weight="bold" />}
      </button>
      <div className="min-w-0 flex-1">
        <div
          className={`truncate text-sm font-semibold ${t.done ? "text-zinc-500 line-through" : ""}`}
        >
          {t.title}
        </div>
        {t.notes && (
          <p className="mt-0.5 line-clamp-2 text-xs text-zinc-500">{t.notes}</p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-widest text-zinc-500">
          <span>{t.assignee || "Whole shop"}</span>
          {t.job_code && <span className="text-indigo-300">{t.job_code}</span>}
          <span>{t.date}</span>
        </div>
      </div>
      <button
        data-testid={`pop-task-delete-${t.id}`}
        onClick={() => remove(t)}
        className="shrink-0 text-zinc-600 transition-colors duration-150 hover:text-red-400"
      >
        <Trash size={15} weight="bold" />
      </button>
    </div>
  );

  const fields = (withAssignee) => (
    <div className="space-y-3">
      <Input
        data-testid="pop-task-title"
        required
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        placeholder="Add a task..."
        className="h-12 border-zinc-800 bg-zinc-950"
      />
      <Textarea
        data-testid="pop-task-notes"
        rows={2}
        value={draft.notes}
        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        placeholder="Description (optional)"
        className="border-zinc-800 bg-zinc-950"
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          value={draft.job_id}
          onValueChange={(v) => setDraft({ ...draft, job_id: v })}
        >
          <SelectTrigger
            data-testid="pop-task-job"
            className="h-11 border-zinc-800 bg-zinc-950"
          >
            <SelectValue placeholder="Tag a job" />
          </SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900">
            <SelectItem value="none">Tag a job…</SelectItem>
            {jobs.map((j) => (
              <SelectItem key={j.id} value={j.id}>
                {j.code} — {j.project_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="pop-task-date"
          type="date"
          value={draft.date}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
          className="h-11 border-zinc-800 bg-zinc-950"
        />
      </div>
      {withAssignee && (
        <Select
          value={draft.assignee}
          onValueChange={(v) => setDraft({ ...draft, assignee: v })}
        >
          <SelectTrigger
            data-testid="pop-task-assignee"
            className="h-11 border-zinc-800 bg-zinc-950"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900">
            <SelectItem value="none">Whole shop</SelectItem>
            {crew.map((u) => (
              <SelectItem key={u.id} value={u.name || u.email}>
                {u.name || u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Button
        data-testid="pop-add-task-button"
        type="submit"
        disabled={busy}
        className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
      >
        <Plus size={15} weight="bold" className="mr-2" /> Add Task
      </Button>
    </div>
  );

  return (
    <div className="relative">
      <button
        data-testid="tasks-popover-trigger"
        onClick={() => {
          setOpen((o) => !o);
          markSeen();
        }}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-zinc-300 transition-colors duration-150 hover:border-indigo-400/40 hover:text-indigo-300"
        aria-label="My tasks"
      >
        <ListChecks size={18} weight="bold" />
        {newlyAssigned.length > 0 && (
          <span
            data-testid="tasks-new-dot"
            className="absolute -left-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full bg-red-500 ring-2 ring-zinc-950"
          />
        )}
        {openCount > 0 && (
          <span
            data-testid="tasks-open-count"
            className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-bold text-white"
          >
            {openCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid="tasks-popover-panel"
            className="absolute right-0 z-50 mt-2 w-[min(92vw,26rem)] rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListChecks
                  size={17}
                  weight="bold"
                  className="text-indigo-300"
                />
                <span className="text-sm font-bold">My Tasks</span>
              </div>
              <button
                data-testid="tasks-popover-close"
                onClick={() => setOpen(false)}
                className="text-zinc-500 hover:text-white"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <div className="mt-4 flex gap-2 border-b border-zinc-800">
              {TABS.map((t) => (
                <button
                  key={t}
                  data-testid={`tasks-tab-${t.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => setTab(t)}
                  className={`-mb-px border-b-2 px-3 pb-2 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    tab === t
                      ? "border-indigo-400 text-indigo-300"
                      : "border-transparent text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className="mt-4 max-h-[60vh] overflow-y-auto">
              {tab === "My Tasks" && (
                <form
                  onSubmit={(e) => add(e, true)}
                  data-testid="my-tasks-form"
                >
                  {fields(false)}
                  <div className="mt-4 space-y-2" data-testid="my-tasks-list">
                    {mine.length === 0 ? (
                      <p className="py-6 text-center text-sm text-zinc-500">
                        No tasks yet. Add one above.
                      </p>
                    ) : (
                      mine.map(row)
                    )}
                  </div>
                </form>
              )}

              {tab === "Assigned" && (
                <div className="space-y-2" data-testid="assigned-tasks-list">
                  {assigned.length === 0 ? (
                    <p className="py-6 text-center text-sm text-zinc-500">
                      Nothing assigned to anyone else today.
                    </p>
                  ) : (
                    assigned.map(row)
                  )}
                </div>
              )}

              {tab === "Assign a task" && (
                <form
                  onSubmit={(e) => add(e, false)}
                  data-testid="assign-task-form"
                >
                  {fields(true)}
                </form>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
